'use client';

import { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { RFP_QUESTIONS, QUESTION_TEMPLATES, type RFPQuestion, type QuestionTemplate } from './questions-database';
import { RFP_TEMPLATES, type RFPTemplate } from './rfp-templates';
import { useAuth } from '@/lib/auth';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import Link from 'next/link';

export default function RFPGeneratorPage() {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>(['Critical', 'High']);
  const [selectedTemplate, setSelectedTemplate] = useState<QuestionTemplate>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [includeWeights, setIncludeWeights] = useState(true);
  const [includeScoring, setIncludeScoring] = useState(true);
  const [customQuestions, setCustomQuestions] = useState<RFPQuestion[]>([]);
  const [excludedQuestionIds, setExcludedQuestionIds] = useState<Set<string>>(new Set());
  const [newQuestion, setNewQuestion] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [vendors, setVendors] = useState<string[]>([]);
  const [newVendor, setNewVendor] = useState('');
  const [dealBreakerQuestionIds, setDealBreakerQuestionIds] = useState<Set<string>>(new Set());
  const [rfpDeadline, setRfpDeadline] = useState('');
  const [rfpTitle, setRfpTitle] = useState('');
  const [rfpDescription, setRfpDescription] = useState('');
  const [vendorStatuses, setVendorStatuses] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'rfp-generator',
    toolCategory: 'generator',
  });

  // Premium access control
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<{ plan: 'free' | 'pro' | 'team'; status: string } | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const TIER_HIERARCHY = {
    free: 0,
    pro: 1,
    team: 2,
  };

  // Fetch user subscription status
  useEffect(() => {
    async function fetchSubscription() {
      if (!isAuthenticated || !user) {
        setLoadingSubscription(false);
        return;
      }

      try {
        const response = await fetch('/api/secure/dashboard', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setSubscription(data.data.subscription);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      } finally {
        setLoadingSubscription(false);
      }
    }

    fetchSubscription();
  }, [isAuthenticated, user]);

  // Check if user has Pro or Team access
  const userTier = subscription?.plan || 'free';
  const hasProAccess = TIER_HIERARCHY[userTier] >= TIER_HIERARCHY.pro;

  // Show toast notification
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000); // Auto-dismiss after 4 seconds
  };

  // Get unique categories
  const categories = useMemo(() => {
    return Array.from(new Set(RFP_QUESTIONS.map((q) => q.category)));
  }, []);

  // Filter questions based on selections
  const filteredQuestions = useMemo(() => {
    let questions = [...RFP_QUESTIONS, ...customQuestions];

    // Filter by template
    if (selectedTemplate !== 'All') {
      questions = questions.filter((q) => q.template === selectedTemplate || q.template === 'All');
    }

    // Filter by category
    if (selectedCategories.length > 0) {
      questions = questions.filter((q) => selectedCategories.includes(q.category));
    }

    // Filter by priority
    if (selectedPriorities.length > 0) {
      questions = questions.filter((q) => selectedPriorities.includes(q.priority));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      questions = questions.filter((q) =>
        q.question.toLowerCase().includes(query) ||
        q.category.toLowerCase().includes(query) ||
        q.subcategory.toLowerCase().includes(query) ||
        q.scoringCriteria?.toLowerCase().includes(query)
      );
    }

    // Exclude manually deselected questions
    questions = questions.filter((q) => !excludedQuestionIds.has(q.id));

    return questions.sort((a, b) => {
      // Sort by category, then priority, then subcategory
      if (a.category !== b.category) return a.category.localeCompare(b.category);
      const priorityOrder = { Critical: 0, High: 1, Medium: 2, Low: 3 };
      if (a.priority !== b.priority) return priorityOrder[a.priority] - priorityOrder[b.priority];
      return a.subcategory.localeCompare(b.subcategory);
    });
  }, [selectedTemplate, selectedCategories, selectedPriorities, searchQuery, customQuestions, excludedQuestionIds]);

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const togglePriority = (priority: string) => {
    setSelectedPriorities((prev) =>
      prev.includes(priority) ? prev.filter((p) => p !== priority) : [...prev, priority]
    );
  };

  const selectAllCategories = () => {
    setSelectedCategories(categories);
  };

  const clearAllCategories = () => {
    setSelectedCategories([]);
  };

  const addCustomQuestion = () => {
    if (!newQuestion || !newCategory) return;

    const question: RFPQuestion = {
      id: `custom-${Date.now()}`,
      category: newCategory,
      subcategory: 'Custom',
      question: newQuestion,
      priority: 'High',
      responseType: 'Text',
      weight: 5,
    };

    setCustomQuestions([...customQuestions, question]);
    setNewQuestion('');
    setNewCategory('');
  };

  const removeCustomQuestion = (id: string) => {
    setCustomQuestions(customQuestions.filter((q) => q.id !== id));
  };

  const addVendor = () => {
    if (!newVendor.trim()) return;
    if (vendors.includes(newVendor.trim())) {
      showToast('Vendor already exists', 'error');
      return;
    }
    setVendors([...vendors, newVendor.trim()]);
    setNewVendor('');
    showToast(`✓ Added vendor: ${newVendor.trim()}`, 'success');
  };

  const removeVendor = (vendor: string) => {
    setVendors(vendors.filter((v) => v !== vendor));
    // Remove from status tracking
    const newStatuses = { ...vendorStatuses };
    delete newStatuses[vendor];
    setVendorStatuses(newStatuses);
  };

  const applyRFPTemplate = (template: RFPTemplate) => {
    // Apply template settings
    setSelectedCategories(template.categories);
    setSelectedPriorities(template.priorities);
    setSelectedTemplate(template.questionTemplate as QuestionTemplate);
    setDealBreakerQuestionIds(new Set(template.dealBreakerQuestionIds));

    // Reset excluded questions to start fresh
    setExcludedQuestionIds(new Set());

    // Set RFP title if not already set
    if (!rfpTitle) {
      setRfpTitle(template.name);
    }
    if (!rfpDescription) {
      setRfpDescription(template.description);
    }

    showToast(`✓ Applied template: ${template.name} (${template.estimatedDuration})`, 'success');
  };

  const toggleDealBreaker = (questionId: string) => {
    setDealBreakerQuestionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const generateEmailTemplates = () => {
    const emailTemplates = {
      invitation: `Subject: RFP Invitation - ${rfpTitle || 'Vendor Evaluation'}

Dear [Vendor Name],

We are conducting a vendor evaluation process and would like to invite you to participate in our Request for Proposal (RFP).

RFP Details:
- Title: ${rfpTitle || '[RFP Title]'}
- Description: ${rfpDescription || '[Description]'}
- Number of Questions: ${filteredQuestions.length}
- Submission Deadline: ${rfpDeadline || '[To be determined]'}

The RFP questionnaire is attached. Please provide detailed responses to all questions and return the completed document by the deadline.

We look forward to learning more about your capabilities.

Best regards,
[Your Name]
[Your Company]`,

      reminder: `Subject: Reminder - RFP Submission Due ${rfpDeadline ? 'on ' + new Date(rfpDeadline).toLocaleDateString() : 'Soon'}

Dear [Vendor Name],

This is a friendly reminder that your RFP submission for ${rfpTitle || 'our vendor evaluation'} is due ${rfpDeadline ? 'on ' + new Date(rfpDeadline).toLocaleDateString() : 'soon'}.

If you have any questions or need clarification on any items, please don't hesitate to reach out.

Thank you for your participation in this process.

Best regards,
[Your Name]
[Your Company]`,

      clarification: `Subject: RFP Clarification Request - ${rfpTitle || 'Vendor Evaluation'}

Dear [Vendor Name],

Thank you for your RFP submission. We have reviewed your responses and would like to request clarification on the following items:

1. [Question/Topic]
   - Current Response: [...]
   - Clarification Needed: [...]

2. [Question/Topic]
   - Current Response: [...]
   - Clarification Needed: [...]

Please provide your clarifications by [Date].

Best regards,
[Your Name]
[Your Company]`,

      rejection: `Subject: RFP Process Update - ${rfpTitle || 'Vendor Evaluation'}

Dear [Vendor Name],

Thank you for participating in our RFP process and taking the time to provide detailed responses.

After careful evaluation, we have decided to move forward with other vendors whose offerings more closely align with our current requirements.

We appreciate your interest and may reach out for future opportunities.

Best regards,
[Your Name]
[Your Company]`,

      finalist: `Subject: RFP Next Steps - You've Been Selected as a Finalist

Dear [Vendor Name],

Congratulations! Your RFP submission has been selected for the next phase of our evaluation process.

We would like to schedule:
- A detailed product demonstration
- Technical deep-dive session
- Reference calls with current customers
- Pricing and contract discussions

Please let us know your availability for the week of [Date Range].

We look forward to learning more about your solution.

Best regards,
[Your Name]
[Your Company]`,
    };

    // Create downloadable text file with all templates
    const allTemplates = Object.entries(emailTemplates)
      .map(([type, content]) => `${'='.repeat(60)}\n${type.toUpperCase()} EMAIL TEMPLATE\n${'='.repeat(60)}\n\n${content}\n\n`)
      .join('\n');

    const blob = new Blob([allTemplates], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `RFP_Email_Templates_${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleQuestionExclusion = (id: string) => {
    setExcludedQuestionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const resetExclusions = () => {
    setExcludedQuestionIds(new Set());
  };

  const selectByCategory = (category: string) => {
    const categoryQuestionIds = RFP_QUESTIONS
      .filter((q) => q.category === category)
      .map((q) => q.id);

    setExcludedQuestionIds((prev) => {
      const newSet = new Set(prev);
      categoryQuestionIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  };

  const deselectByCategory = (category: string) => {
    const categoryQuestionIds = RFP_QUESTIONS
      .filter((q) => q.category === category)
      .map((q) => q.id);

    setExcludedQuestionIds((prev) => {
      const newSet = new Set(prev);
      categoryQuestionIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  };

  const selectByPriority = (priority: string) => {
    const priorityQuestionIds = RFP_QUESTIONS
      .filter((q) => q.priority === priority)
      .map((q) => q.id);

    setExcludedQuestionIds((prev) => {
      const newSet = new Set(prev);
      priorityQuestionIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  };

  const deselectByPriority = (priority: string) => {
    const priorityQuestionIds = RFP_QUESTIONS
      .filter((q) => q.priority === priority)
      .map((q) => q.id);

    setExcludedQuestionIds((prev) => {
      const newSet = new Set(prev);
      priorityQuestionIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  };

  const saveConfiguration = () => {
    const config = {
      selectedCategories,
      selectedPriorities,
      selectedTemplate,
      searchQuery,
      includeWeights,
      includeScoring,
      customQuestions,
      excludedQuestionIds: Array.from(excludedQuestionIds),
      vendors,
      dealBreakerQuestionIds: Array.from(dealBreakerQuestionIds),
      rfpDeadline,
      rfpTitle,
      rfpDescription,
      vendorStatuses,
      timestamp: new Date().toISOString(),
    };

    const configJson = JSON.stringify(config, null, 2);
    const blob = new Blob([configJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rfp-config-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const loadConfiguration = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const config = JSON.parse(e.target?.result as string);

        if (config.selectedCategories) setSelectedCategories(config.selectedCategories);
        if (config.selectedPriorities) setSelectedPriorities(config.selectedPriorities);
        if (config.selectedTemplate) setSelectedTemplate(config.selectedTemplate);
        if (config.searchQuery !== undefined) setSearchQuery(config.searchQuery);
        if (config.includeWeights !== undefined) setIncludeWeights(config.includeWeights);
        if (config.includeScoring !== undefined) setIncludeScoring(config.includeScoring);
        if (config.customQuestions) setCustomQuestions(config.customQuestions);
        if (config.excludedQuestionIds) {
          setExcludedQuestionIds(new Set(config.excludedQuestionIds));
        }
        if (config.vendors) setVendors(config.vendors);
        if (config.dealBreakerQuestionIds) {
          setDealBreakerQuestionIds(new Set(config.dealBreakerQuestionIds));
        }
        if (config.rfpDeadline) setRfpDeadline(config.rfpDeadline);
        if (config.rfpTitle) setRfpTitle(config.rfpTitle);
        if (config.rfpDescription) setRfpDescription(config.rfpDescription);
        if (config.vendorStatuses) setVendorStatuses(config.vendorStatuses);

        showToast('✓ Configuration loaded successfully!', 'success');
      } catch (error) {
        showToast('Error loading configuration file. Please ensure it is a valid RFP configuration.', 'error');
        console.error('Error loading config:', error);
      }
    };
    reader.readAsText(file);
  };

  const exportToExcel = () => {
    // Create workbook
    const wb = XLSX.utils.book_new();

    // Set workbook properties for auto-calculation
    // Force formulas to recalculate on load
    if (!wb.Workbook) wb.Workbook = {};
    (wb.Workbook as any).CalcPr = { fullCalcOnLoad: true };

    // Helper function to create vendor sheet data
    const createVendorSheetData = (vendorName?: string) => {
      return filteredQuestions.map((q, index) => {
        const row: any = {
          '#': index + 1,
          'Question ID': q.id,
          'Category': q.category,
          'Subcategory': q.subcategory,
          'Question': q.question,
          'Priority': q.priority,
          'Response Type': q.responseType,
          'Status': '',
          'Vendor Response': '',
          'Response Date': '',
          'Internal Notes': '',
          'Follow-up Required': '',
        };

        if (includeWeights) {
          row['Weight'] = q.weight || 0;
        }

        if (includeScoring) {
          row['Scoring Criteria'] = q.scoringCriteria || '';
          row['Score (0-10)'] = '';

          // Calculate column letter for weighted score formula
          let scoreCol = 'M'; // Default if both weights and scoring
          if (!includeWeights) {
            scoreCol = 'L'; // Shift left if no weights
          }
          row['Weighted Score'] = includeWeights ? `=IF(ISNUMBER(${scoreCol}${index + 2}),${scoreCol}${index + 2}*H${index + 2}/10,"")` : '';
        }

        return row;
      });
    };

    // If no vendors, create single-sheet version
    if (vendors.length === 0) {
      const enhancedData = createVendorSheetData();
      const ws = XLSX.utils.json_to_sheet(enhancedData);

    // Set column widths
    const colWidths = [
      { wch: 5 },  // #
      { wch: 12 }, // Question ID
      { wch: 25 }, // Category
      { wch: 20 }, // Subcategory
      { wch: 60 }, // Question
      { wch: 10 }, // Priority
      { wch: 15 }, // Response Type
      { wch: 15 }, // Status
      { wch: 40 }, // Vendor Response
      { wch: 12 }, // Response Date
      { wch: 30 }, // Internal Notes
      { wch: 12 }, // Follow-up Required
    ];

    if (includeWeights) {
      colWidths.push({ wch: 10 }); // Weight
    }

    if (includeScoring) {
      colWidths.push({ wch: 40 }); // Scoring Criteria
      colWidths.push({ wch: 12 }); // Score
      colWidths.push({ wch: 15 }); // Weighted Score
    }

    ws['!cols'] = colWidths;

    // Add data validation for Status column (column H)
    const statusValidation = {
      type: 'list',
      allowBlank: true,
      formula1: '"Complete,In Progress,Pending,Not Applicable"',
      showDropDown: true,
    };

    // Add data validation for Follow-up Required column (column L)
    const followUpValidation = {
      type: 'list',
      allowBlank: true,
      formula1: '"Yes,No"',
      showDropDown: true,
    };

    // Note: Data validation needs to be added manually in Excel or using a library like exceljs
    // xlsx library has limited support for data validation
    // Adding validation info as a comment for users

    // Add autofilter
    ws['!autofilter'] = { ref: `A1:${String.fromCharCode(65 + Object.keys(enhancedData[0] || {}).length - 1)}${filteredQuestions.length + 1}` };

    // Add sheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'RFP Questions');

    // Create Instructions sheet
    const instructions = [
      ['RFP Question Generator - Instructions'],
      [''],
      ['COLUMN GUIDE:'],
      [''],
      ['#', 'Question number for reference'],
      ['Question ID', 'Unique identifier for each question'],
      ['Category', 'Question category (Company, Technical, Security, etc.)'],
      ['Subcategory', 'More specific grouping within category'],
      ['Question', 'The actual question to ask the vendor'],
      ['Priority', 'Importance level: Critical, High, Medium, Low'],
      ['Response Type', 'Expected type of answer (Text, Yes/No, Numeric, List, Document)'],
      ['Status', 'DROPDOWN: Complete, In Progress, Pending, Not Applicable'],
      ['Vendor Response', 'Vendor\'s answer to the question'],
      ['Response Date', 'Date vendor provided the response'],
      ['Internal Notes', 'Your internal evaluation notes'],
      ['Follow-up Required', 'DROPDOWN: Yes, No'],
      ['Weight', 'Question importance (0-10 scale)'],
      ['Scoring Criteria', 'How to evaluate the response'],
      ['Score (0-10)', 'Your score for this answer'],
      ['Weighted Score', 'AUTO-CALCULATED: Score × Weight / 10'],
      [''],
      ['HOW TO ADD DROPDOWNS:'],
      [''],
      ['Status Column (H):'],
      ['1. Select cells H2 to H' + (filteredQuestions.length + 1)],
      ['2. Go to Data > Data Validation'],
      ['3. Select "List" and enter: Complete,In Progress,Pending,Not Applicable'],
      [''],
      ['Follow-up Required Column (L):'],
      ['1. Select cells L2 to L' + (filteredQuestions.length + 1)],
      ['2. Go to Data > Data Validation'],
      ['3. Select "List" and enter: Yes,No'],
      [''],
      ['Score Column:'],
      ['1. Select the Score column cells'],
      ['2. Go to Data > Data Validation'],
      ['3. Select "Whole number" between 0 and 10'],
      [''],
      ['FEATURES:'],
      [''],
      ['✓ Auto-filtering enabled on all columns'],
      ['✓ Formulas in Weighted Score column (do not edit)'],
      ['✓ Scoring sheet with total calculations'],
      ['✓ Summary sheet with question breakdown'],
      [''],
      ['TIPS:'],
      [''],
      ['• Use filters to focus on specific categories or priorities'],
      ['• Mark Status as you progress through questions'],
      ['• Add Response Date to track when answers were received'],
      ['• Use Follow-up Required to mark questions needing clarification'],
      ['• Check the Scoring sheet for overall vendor evaluation'],
      [''],
      ['Generated: ' + new Date().toLocaleDateString()],
    ];

    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
    wsInstructions['!cols'] = [{ wch: 30 }, { wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

    // Create summary sheet
    const summary = [
      ['RFP Question Summary'],
      [''],
      ['Total Questions', filteredQuestions.length],
      ['Categories', selectedCategories.length > 0 ? selectedCategories.join(', ') : 'All'],
      ['Priority Levels', selectedPriorities.join(', ')],
      [''],
      ['Breakdown by Category'],
    ];

    // Count questions per category
    const categoryCount: Record<string, number> = {};
    filteredQuestions.forEach((q) => {
      categoryCount[q.category] = (categoryCount[q.category] || 0) + 1;
    });

    Object.entries(categoryCount).forEach(([cat, count]) => {
      summary.push([cat, count]);
    });

    summary.push(['']);
    summary.push(['Breakdown by Priority']);

    // Count questions per priority
    const priorityCount: Record<string, number> = {};
    filteredQuestions.forEach((q) => {
      priorityCount[q.priority] = (priorityCount[q.priority] || 0) + 1;
    });

    Object.entries(priorityCount).forEach(([priority, count]) => {
      summary.push([priority, count]);
    });

    const wsSummary = XLSX.utils.aoa_to_sheet(summary);
    wsSummary['!cols'] = [{ wch: 30 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Create scoring sheet (if enabled)
    if (includeScoring && includeWeights) {
      const scoringData = [
        ['Vendor Scoring Sheet'],
        [''],
        ['Instructions:', 'Score each question from 0-10 based on the scoring criteria. The weighted total will be calculated automatically.'],
        [''],
        ['Category', 'Question', 'Weight', 'Score (0-10)', 'Weighted Score'],
      ];

      filteredQuestions.forEach((q) => {
        scoringData.push([
          q.category,
          q.question,
          (q.weight || 0).toString(),
          '', // Score to be filled
          `=D${scoringData.length + 1}*C${scoringData.length + 1}/10`, // Formula for weighted score
        ]);
      });

      const totalRow = scoringData.length + 1;
      scoringData.push(['', 'TOTAL', `=SUM(C6:C${totalRow - 1})`, '', `=SUM(E6:E${totalRow - 1})`]);
      scoringData.push(['', 'PERCENTAGE', '', '', `=E${totalRow}/C${totalRow}*100&"%"`]);

      const wsScoring = XLSX.utils.aoa_to_sheet(scoringData);
      wsScoring['!cols'] = [{ wch: 25 }, { wch: 60 }, { wch: 10 }, { wch: 12 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsScoring, 'Scoring');
    }
    } else {
      // Multi-vendor mode: Create a sheet for each vendor plus a comparison summary

      // Column widths for vendor sheets
      const colWidths = [
        { wch: 5 },  // #
        { wch: 12 }, // Question ID
        { wch: 25 }, // Category
        { wch: 20 }, // Subcategory
        { wch: 60 }, // Question
        { wch: 10 }, // Priority
        { wch: 15 }, // Response Type
        { wch: 15 }, // Status
        { wch: 40 }, // Vendor Response
        { wch: 12 }, // Response Date
        { wch: 30 }, // Internal Notes
        { wch: 12 }, // Follow-up Required
      ];

      if (includeWeights) {
        colWidths.push({ wch: 10 }); // Weight
      }

      if (includeScoring) {
        colWidths.push({ wch: 40 }); // Scoring Criteria
        colWidths.push({ wch: 12 }); // Score
        colWidths.push({ wch: 15 }); // Weighted Score
      }

      // Create a sheet for each vendor
      vendors.forEach((vendor) => {
        const vendorData = createVendorSheetData(vendor);
        const ws = XLSX.utils.json_to_sheet(vendorData);
        ws['!cols'] = colWidths;

        // Add autofilter
        ws['!autofilter'] = { ref: `A1:${String.fromCharCode(65 + Object.keys(vendorData[0] || {}).length - 1)}${filteredQuestions.length + 1}` };

        // Sanitize sheet name (max 31 chars, no special chars)
        const sheetName = vendor.substring(0, 31).replace(/[:\\/?*\[\]]/g, '_');
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      // Create vendor comparison summary sheet
      if (includeScoring && includeWeights) {
        const summaryData: any[] = [
          ['Vendor Comparison Summary'],
          [''],
          ['Category', ...vendors, 'Average'],
          [''],
        ];

        // Get unique categories from filtered questions
        const categoriesInQuestions = Array.from(new Set(filteredQuestions.map(q => q.category)));

        categoriesInQuestions.forEach((category) => {
          const categoryQuestions = filteredQuestions.filter(q => q.category === category);
          const maxScore = categoryQuestions.reduce((sum, q) => sum + (q.weight || 0), 0);

          const row: any[] = [category];

          // For each vendor, add formula to sum their weighted scores for this category
          vendors.forEach((vendor, vendorIndex) => {
            const sheetName = vendor.substring(0, 31).replace(/[:\\/?*\[\]]/g, '_');

            // Build formula to sum weighted scores for this category from vendor sheet
            // We need to find which rows have this category
            const categoryRows: number[] = [];
            filteredQuestions.forEach((q, qIndex) => {
              if (q.category === category) {
                categoryRows.push(qIndex + 2); // +2 for header row
              }
            });

            if (categoryRows.length > 0) {
              // Calculate the weighted score column letter
              let wsCol = 'O'; // Default position
              if (!includeWeights) wsCol = 'N';

              const sumParts = categoryRows.map(rowNum => `'${sheetName}'!${wsCol}${rowNum}`);
              row.push(`=SUM(${sumParts.join(',')})`);
            } else {
              row.push(0);
            }
          });

          // Add average formula
          const startCol = String.fromCharCode(66); // B
          const endCol = String.fromCharCode(66 + vendors.length - 1);
          const currentRow = summaryData.length + 1;
          row.push(`=AVERAGE(${startCol}${currentRow}:${endCol}${currentRow})`);

          summaryData.push(row);
        });

        // Add total row
        summaryData.push(['']);
        const totalRow: any[] = ['TOTAL SCORE'];
        vendors.forEach((_, vendorIndex) => {
          const col = String.fromCharCode(66 + vendorIndex); // B, C, D, etc.
          const startRow = 5; // First data row
          const endRow = summaryData.length;
          totalRow.push(`=SUM(${col}${startRow}:${col}${endRow})`);
        });

        // Average total
        const startCol = String.fromCharCode(66);
        const endCol = String.fromCharCode(66 + vendors.length - 1);
        const totalRowNum = summaryData.length + 1;
        totalRow.push(`=AVERAGE(${startCol}${totalRowNum}:${endCol}${totalRowNum})`);

        summaryData.push(totalRow);

        // Add ranking row
        summaryData.push(['']);
        summaryData.push(['RANKING']);
        const rankRow: any[] = [''];
        vendors.forEach((_, vendorIndex) => {
          const col = String.fromCharCode(66 + vendorIndex);
          const totalRowRef = summaryData.length - 1;
          rankRow.push(`=RANK(${col}${totalRowRef},$B$${totalRowRef}:$${String.fromCharCode(66 + vendors.length - 1)}$${totalRowRef},0)`);
        });
        rankRow.push('');
        summaryData.push(rankRow);

        const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);

        // Set column widths
        const summaryColWidths = [{ wch: 30 }]; // Category column
        vendors.forEach(() => summaryColWidths.push({ wch: 15 })); // Vendor columns
        summaryColWidths.push({ wch: 15 }); // Average column
        wsSummary['!cols'] = summaryColWidths;

        XLSX.utils.book_append_sheet(wb, wsSummary, 'Vendor Comparison');

        // ==================== FINAL REPORT TAB ====================
        // Create comprehensive final report with executive summary
        const reportData: any[] = [
          ['RFP EVALUATION - FINAL REPORT'],
          [''],
          ['=' + '='.repeat(80)],
          ['EXECUTIVE SUMMARY'],
          ['=' + '='.repeat(80)],
          [''],
          ['RFP Title:', rfpTitle || 'Vendor Evaluation'],
          ['Description:', rfpDescription || 'N/A'],
          ['Evaluation Date:', new Date().toLocaleDateString()],
          ['Submission Deadline:', rfpDeadline ? new Date(rfpDeadline).toLocaleDateString() : 'N/A'],
          ['Total Questions:', filteredQuestions.length],
          ['Vendors Evaluated:', vendors.length],
          ['Deal-Breaker Questions:', dealBreakerQuestionIds.size],
          [''],
          ['=' + '='.repeat(80)],
          ['VENDOR RANKINGS'],
          ['=' + '='.repeat(80)],
          [''],
          ['Rank', 'Vendor Name', 'Total Score', 'Status'],
        ];

        // Add vendor rankings (formulas will calculate from Vendor Comparison sheet)
        vendors.forEach((vendor, idx) => {
          const col = String.fromCharCode(66 + idx); // B, C, D, etc.
          const totalRowRef = 5 + categoriesInQuestions.length + 1; // Row with TOTAL SCORE in Vendor Comparison
          reportData.push([
            `=RANK('Vendor Comparison'!${col}$${totalRowRef},'Vendor Comparison'!$B$${totalRowRef}:$${String.fromCharCode(66 + vendors.length - 1)}$${totalRowRef},0)`,
            vendor,
            `='Vendor Comparison'!${col}${totalRowRef}`,
            vendorStatuses[vendor] || 'Pending',
          ]);
        });

        reportData.push(['']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['DEAL-BREAKER ANALYSIS']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['']);

        const dealBreakerQuestions = filteredQuestions.filter(q => dealBreakerQuestionIds.has(q.id));

        if (dealBreakerQuestions.length > 0) {
          reportData.push(['Critical Requirements (Must Pass):']);
          reportData.push(['']);
          reportData.push(['Question ID', 'Question', 'Category', 'Priority']);

          dealBreakerQuestions.forEach(q => {
            reportData.push([q.id, q.question, q.category, q.priority]);
          });

          reportData.push(['']);
          reportData.push(['Note: Vendors must score 7+ on ALL deal-breaker questions to be considered viable.']);
        } else {
          reportData.push(['No deal-breaker questions defined.']);
        }

        reportData.push(['']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['CATEGORY PERFORMANCE BREAKDOWN']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['']);
        reportData.push(['Category', 'Questions', 'Max Possible Score', ...vendors.map(v => `${v} Score`), 'Category Weight']);

        categoriesInQuestions.forEach((category, catIdx) => {
          const categoryQuestions = filteredQuestions.filter(q => q.category === category);
          const maxScore = categoryQuestions.reduce((sum, q) => sum + (q.weight || 0), 0);
          const row: any[] = [
            category,
            categoryQuestions.length,
            maxScore,
          ];

          // Add formulas referencing Vendor Comparison sheet
          vendors.forEach((vendor, vendorIdx) => {
            const col = String.fromCharCode(66 + vendorIdx);
            const summaryRow = 5 + catIdx; // Row in Vendor Comparison sheet
            row.push(`='Vendor Comparison'!${col}${summaryRow}`);
          });

          // Category weight as percentage of total
          row.push(`=${maxScore}/SUM(C${reportData.length + 2}:C${reportData.length + categoriesInQuestions.length + 1})`);

          reportData.push(row);
        });

        reportData.push(['']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['KEY FINDINGS & RECOMMENDATIONS']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['']);
        reportData.push(['Based on the evaluation criteria, consider the following:']);
        reportData.push(['']);
        reportData.push(['1. TOP PERFORMER:']);
        reportData.push(['   • Vendor with highest total score shows strongest overall alignment']);
        reportData.push(['   • Review category breakdowns for specific strengths']);
        reportData.push(['']);
        reportData.push(['2. DEAL-BREAKER COMPLIANCE:']);
        reportData.push(['   • Verify all deal-breaker questions scored 7+ for final candidates']);
        reportData.push(['   • Any vendor failing critical requirements should be eliminated']);
        reportData.push(['']);
        reportData.push(['3. CATEGORY ANALYSIS:']);
        reportData.push(['   • Check which vendor excels in your highest-priority categories']);
        reportData.push(['   • Consider weighting category importance based on business needs']);
        reportData.push(['']);
        reportData.push(['4. NEXT STEPS:']);
        reportData.push(['   • Schedule demos with top 2-3 vendors']);
        reportData.push(['   • Conduct reference calls']);
        reportData.push(['   • Request pricing and contract terms']);
        reportData.push(['   • Perform technical deep-dive sessions']);
        reportData.push(['']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['SCORING LEGEND']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['']);
        reportData.push(['Score', 'Rating', 'Description']);
        reportData.push(['9-10', 'Excellent', 'Exceeds requirements significantly']);
        reportData.push(['7-8', 'Good', 'Meets all requirements']);
        reportData.push(['5-6', 'Acceptable', 'Meets most requirements with minor gaps']);
        reportData.push(['3-4', 'Poor', 'Significant gaps in requirements']);
        reportData.push(['0-2', 'Unacceptable', 'Does not meet requirements']);
        reportData.push(['']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['RISK ASSESSMENT']);
        reportData.push(['=' + '='.repeat(80)]);
        reportData.push(['']);
        reportData.push(['Consider these risk factors for each vendor:']);
        reportData.push(['']);
        reportData.push(['• Company Stability: Years in business, funding, customer retention']);
        reportData.push(['• Technical Maturity: Architecture, scalability, performance track record']);
        reportData.push(['• Security Posture: Certifications, breach history, compliance']);
        reportData.push(['• Support Quality: SLA guarantees, response times, escalation process']);
        reportData.push(['• Vendor Lock-in: Data portability, API availability, migration path']);
        reportData.push(['']);
        reportData.push(['Generated:', new Date().toLocaleString()]);

        const wsReport = XLSX.utils.aoa_to_sheet(reportData);

        // Set column widths for final report
        const reportColWidths = [
          { wch: 25 }, // Column A
          { wch: 60 }, // Column B
          { wch: 20 }, // Column C
        ];
        vendors.forEach(() => reportColWidths.push({ wch: 15 }));
        wsReport['!cols'] = reportColWidths;

        XLSX.utils.book_append_sheet(wb, wsReport, 'Final Report');

        // ==================== VISUAL DASHBOARD ====================
        // Create a visual dashboard with text-based charts
        const dashboardData: any[] = [
          ['📊 VENDOR COMPARISON DASHBOARD'],
          [''],
          ['━'.repeat(100)],
          ['VENDOR RANKINGS - Visual Comparison'],
          ['━'.repeat(100)],
          [''],
        ];

        // Add visual bars for each vendor (will show after scoring)
        dashboardData.push(['Vendor', 'Score', 'Visual Representation (fill scores to see bars)', 'Status']);
        vendors.forEach((vendor, idx) => {
          const col = String.fromCharCode(66 + idx);
          const totalRowRef = 5 + categoriesInQuestions.length + 1;
          const scoreFormula = `='Vendor Comparison'!${col}${totalRowRef}`;

          // Create a bar chart using REPT function (repeats ▓ character based on score/10)
          const barFormula = `=REPT("▓",ROUND(${scoreFormula}/10,0))&" "&TEXT(${scoreFormula},"0.0")&"/100"`;

          dashboardData.push([
            vendor,
            scoreFormula,
            barFormula,
            vendorStatuses[vendor] || 'Pending',
          ]);
        });

        dashboardData.push(['']);
        dashboardData.push(['━'.repeat(100)]);
        dashboardData.push(['CATEGORY PERFORMANCE HEATMAP']);
        dashboardData.push(['━'.repeat(100)]);
        dashboardData.push(['']);
        dashboardData.push(['Category', ...vendors.map(v => v), 'Best Performer']);

        // Add category comparison with visual indicators
        categoriesInQuestions.forEach((category, catIdx) => {
          const row: any[] = [category];
          const summaryRow = 5 + catIdx;

          vendors.forEach((vendor, vendorIdx) => {
            const col = String.fromCharCode(66 + vendorIdx);
            // Show score with visual indicator
            const scoreRef = `'Vendor Comparison'!${col}${summaryRow}`;
            row.push(`=TEXT(${scoreRef},"0.0")&" "&IF(${scoreRef}>=80,"🟢",IF(${scoreRef}>=60,"🟡","🔴"))`);
          });

          // Best performer for this category
          const startCol = String.fromCharCode(66);
          const endCol = String.fromCharCode(66 + vendors.length - 1);
          const currentRow = dashboardData.length + 1;
          const maxFormula = `=INDEX($B$${dashboardData.length}:$${endCol}$${dashboardData.length},1,MATCH(MAX(VALUE(LEFT($B${currentRow}:$${endCol}${currentRow},FIND(" ",$B${currentRow}:$${endCol}${currentRow})-1))),$B$${summaryRow}:$${endCol}$${summaryRow},0))`;
          row.push(`="➔ "&INDEX($B$7:$${endCol}$7,1,MATCH(MAX('Vendor Comparison'!$B$${summaryRow}:$${endCol}$${summaryRow}),'Vendor Comparison'!$B$${summaryRow}:$${endCol}$${summaryRow},0))`);

          dashboardData.push(row);
        });

        dashboardData.push(['']);
        dashboardData.push(['']);
        dashboardData.push(['Legend:']);
        dashboardData.push(['🟢 Excellent (80-100)', '🟡 Good (60-79)', '🔴 Needs Improvement (<60)']);
        dashboardData.push(['▓ = 10 points', 'Each block represents 10 points in total score visualization']);
        dashboardData.push(['']);
        dashboardData.push(['Generated:', new Date().toLocaleString()]);

        const wsDashboard = XLSX.utils.aoa_to_sheet(dashboardData);
        const dashboardColWidths = [{ wch: 30 }]; // Category column
        vendors.forEach(() => dashboardColWidths.push({ wch: 20 })); // Vendor columns
        dashboardColWidths.push({ wch: 25 }); // Best performer column
        wsDashboard['!cols'] = dashboardColWidths;

        XLSX.utils.book_append_sheet(wb, wsDashboard, 'Visual Dashboard');
      }

      // Create Instructions sheet for multi-vendor
      const instructions = [
        ['RFP Multi-Vendor Comparison - Instructions'],
        [''],
        ['OVERVIEW:'],
        [''],
        ['This workbook contains:'],
        [`• ${vendors.length} vendor sheet(s): ${vendors.join(', ')}`],
        ['• Vendor Comparison summary sheet (if scoring enabled)'],
        ['• Visual Dashboard with charts and heatmaps'],
        ['• Final Report with executive summary'],
        [''],
        ['VENDOR SHEETS:'],
        [''],
        ['Each vendor has their own sheet with all RFP questions.'],
        ['Fill in responses, scores, and status for each vendor independently.'],
        [''],
        ['COLUMN GUIDE:'],
        [''],
        ['#', 'Question number for reference'],
        ['Question ID', 'Unique identifier'],
        ['Category', 'Question category'],
        ['Subcategory', 'Specific grouping'],
        ['Question', 'The RFP question'],
        ['Priority', 'Critical, High, Medium, Low'],
        ['Response Type', 'Expected answer type'],
        ['Status', 'Complete, In Progress, Pending, Not Applicable'],
        ['Vendor Response', 'Vendor\'s answer'],
        ['Response Date', 'Date of response'],
        ['Internal Notes', 'Your evaluation notes'],
        ['Follow-up Required', 'Yes/No'],
        ['Weight', 'Question importance (0-10)'],
        ['Scoring Criteria', 'How to evaluate'],
        ['Score (0-10)', 'Your score'],
        ['Weighted Score', 'AUTO-CALCULATED: Score × Weight / 10'],
        [''],
        ['VENDOR COMPARISON SHEET:'],
        [''],
        ['• Shows total weighted scores by category for each vendor'],
        ['• Automatically calculated from individual vendor sheets'],
        ['• Includes average scores and vendor rankings'],
        ['• Higher scores = better performance'],
        [''],
        ['VISUAL DASHBOARD:'],
        [''],
        ['• Bar charts showing vendor total scores (▓ blocks)'],
        ['• Category performance heatmap with color indicators'],
        ['• 🟢 Green = Excellent (80-100), 🟡 Yellow = Good (60-79), 🔴 Red = Needs Improvement (<60)'],
        ['• Best performer highlighted for each category'],
        ['• Automatic visual updates as you fill in scores'],
        [''],
        ['TIPS:'],
        [''],
        ['• Complete all vendor sheets before reviewing comparison'],
        ['• Use consistent scoring criteria across all vendors'],
        ['• Check the ranking row to see overall winner'],
        ['• Review category breakdowns to identify strengths/weaknesses'],
        [''],
        ['Generated: ' + new Date().toLocaleDateString()],
        [`Total Questions: ${filteredQuestions.length}`],
        [`Vendors: ${vendors.length}`],
      ];

      const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
      wsInstructions['!cols'] = [{ wch: 30 }, { wch: 70 }];
      XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');
    }

    // Write file
    const filename = vendors.length > 0
      ? `RFP_Multi_Vendor_${new Date().toISOString().split('T')[0]}.xlsx`
      : `RFP_Questions_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    // Summarize questions by category
    const categoryBreakdown: Record<string, number> = {};
    const priorityBreakdown: Record<string, number> = {};
    filteredQuestions.forEach((q) => {
      categoryBreakdown[q.category] = (categoryBreakdown[q.category] || 0) + 1;
      priorityBreakdown[q.priority] = (priorityBreakdown[q.priority] || 0) + 1;
    });

    const assessment = await saveResult({
      name: rfpTitle || `RFP - ${new Date().toLocaleDateString()}`,
      metadata: {
        rfpTitle: rfpTitle || 'Untitled RFP',
        rfpDescription,
        rfpDeadline,
        totalQuestions: filteredQuestions.length,
        selectedCategories,
        selectedPriorities,
        selectedTemplate,
        categoryBreakdown,
        priorityBreakdown,
        vendors,
        vendorStatuses,
        dealBreakerCount: dealBreakerQuestionIds.size,
        dealBreakerQuestions: Array.from(dealBreakerQuestionIds).map(id => {
          const q = filteredQuestions.find(q => q.id === id);
          return q ? { id: q.id, question: q.question, category: q.category } : null;
        }).filter(Boolean),
        customQuestionsCount: customQuestions.length,
        includeWeights,
        includeScoring,
        questions: filteredQuestions.map(q => ({
          id: q.id,
          category: q.category,
          subcategory: q.subcategory,
          question: q.question,
          priority: q.priority,
          responseType: q.responseType,
          weight: q.weight,
          isDealBreaker: dealBreakerQuestionIds.has(q.id),
        })),
      },
    });

    if (assessment) {
      setSaved(true);
      showToast('RFP saved to Command Center', 'success');
    }
  };

  return (
    <div className="min-h-screen bg-background py-12">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg border-2 transform transition-all duration-300 ${
            toast.type === 'success'
              ? 'bg-green-900/90 border-green-500 text-green-100'
              : toast.type === 'error'
              ? 'bg-red-900/90 border-red-500 text-red-100'
              : 'bg-blue-900/90 border-blue-500 text-blue-100'
          }`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '⚠' : 'ℹ'}
            </span>
            <span className="font-medium">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-4 text-xl opacity-70 hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">RFP Question Generator</h1>
          <p className="text-foreground-secondary text-lg">
            Generate comprehensive vendor evaluation questionnaires. Select categories, customize questions, and export to Excel with professional formatting.
          </p>
        </div>

        {/* Premium Feature Banner */}
        {!hasProAccess && (
          <div className="mb-6 bg-gradient-to-r from-accent-muted to-accent/5 border-2 border-accent/40 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-accent" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-foreground font-semibold">Excel Export is a Pro Feature</p>
                  <p className="text-foreground-secondary text-sm">Upgrade to Pro or Team to export your RFP with automated formulas, multi-vendor comparison, and visual dashboards.</p>
                </div>
              </div>
              <Link
                href="/pricing"
                className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors whitespace-nowrap"
              >
                Upgrade Now
              </Link>
            </div>
          </div>
        )}

        {/* RFP Details */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">RFP Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-foreground text-sm font-medium mb-2">RFP Title</label>
              <input
                type="text"
                placeholder="e.g., Cloud Infrastructure Provider RFP"
                value={rfpTitle}
                onChange={(e) => setRfpTitle(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
              />
            </div>
            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Submission Deadline</label>
              <input
                type="date"
                value={rfpDeadline}
                onChange={(e) => setRfpDeadline(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              />
            </div>
          </div>
          <div>
            <label className="block text-foreground text-sm font-medium mb-2">Description</label>
            <textarea
              placeholder="Brief description of the RFP purpose and objectives..."
              value={rfpDescription}
              onChange={(e) => setRfpDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
          </div>
        </div>

        {/* Pre-Built RFP Templates */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">Pre-Built RFP Templates</h2>
          <p className="text-foreground-secondary text-sm mb-4">
            Quick-start with industry-standard RFP templates. Each template pre-selects relevant categories, priorities, and deal-breaker questions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {RFP_TEMPLATES.map((template) => (
              <div
                key={template.id}
                className="p-4 bg-background rounded border border-border-muted hover:border-accent/40 transition-colors cursor-pointer"
                onClick={() => applyRFPTemplate(template)}
              >
                <h3 className="text-lg font-semibold text-accent mb-2">{template.name}</h3>
                <p className="text-foreground-secondary text-sm mb-3">{template.description}</p>
                <div className="flex items-center justify-between text-xs text-foreground-tertiary">
                  <span>{template.categories.length} categories</span>
                  <span className="px-2 py-1 bg-accent-muted text-accent rounded">{template.estimatedDuration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Email Templates Button */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-accent mb-2">Email Templates</h2>
              <p className="text-foreground-secondary text-sm">
                Download pre-written email templates for vendor invitations, reminders, and follow-ups.
              </p>
            </div>
            <button
              onClick={generateEmailTemplates}
              className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Download Email Templates
            </button>
          </div>
        </div>

        {/* Template and Search Filters */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">Question Template</h2>
          <p className="text-foreground-secondary text-sm mb-4">
            Filter questions by vendor type. Each template includes general questions plus specialized ones for that category.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            {QUESTION_TEMPLATES.map((template) => (
              <button
                key={template}
                onClick={() => setSelectedTemplate(template)}
                className={`px-4 py-2 rounded transition-colors font-medium ${
                  selectedTemplate === template
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-background text-foreground border border-border hover:bg-surfaceer'
                }`}
              >
                {template}
              </button>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-accent mb-4">Search Questions</h2>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by question text, category, or scoring criteria..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 bg-background border border-border rounded text-foreground placeholder-foreground-muted pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-tertiary hover:text-foreground"
                title="Clear search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Category and Priority Filters */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">Select Categories</h2>

          <div className="flex gap-2 mb-4">
            <button
              onClick={selectAllCategories}
              className="px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors"
            >
              Select All
            </button>
            <button
              onClick={clearAllCategories}
              className="px-4 py-2 bg-cream/20 text-foreground rounded hover:bg-cream/30 transition-colors"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {categories.map((category) => (
              <label
                key={category}
                className="flex items-center gap-2 p-3 bg-background rounded cursor-pointer hover:bg-surfaceer transition-colors border border-border-muted"
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category)}
                  onChange={() => toggleCategory(category)}
                  className="w-4 h-4"
                />
                <span className="text-foreground">{category}</span>
                <span className="ml-auto text-foreground-tertiary text-sm">
                  ({RFP_QUESTIONS.filter((q) => q.category === category).length})
                </span>
              </label>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-accent mb-4 mt-8">Select Priority Levels</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['Critical', 'High', 'Medium', 'Low'].map((priority) => (
              <label
                key={priority}
                className="flex items-center gap-2 p-3 bg-background rounded cursor-pointer hover:bg-surfaceer transition-colors border border-border-muted"
              >
                <input
                  type="checkbox"
                  checked={selectedPriorities.includes(priority)}
                  onChange={() => togglePriority(priority)}
                  className="w-4 h-4"
                />
                <span className="text-foreground">{priority}</span>
              </label>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-accent mb-4 mt-8">Save/Load Configuration</h2>
          <p className="text-foreground-secondary text-sm mb-4">
            Save your current filter settings and question selections to reuse later, or load a previously saved configuration.
          </p>
          <div className="flex gap-3 mb-8">
            <button
              onClick={saveConfiguration}
              className="px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              Save Configuration
            </button>
            <label className="px-4 py-2 bg-cream/20 text-foreground rounded hover:bg-cream/30 transition-colors cursor-pointer font-medium flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Load Configuration
              <input
                type="file"
                accept=".json"
                onChange={loadConfiguration}
                className="hidden"
              />
            </label>
          </div>

          <h2 className="text-2xl font-bold text-accent mb-4">Export Options</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 p-3 bg-background rounded cursor-pointer hover:bg-surfaceer transition-colors border border-border-muted">
              <input
                type="checkbox"
                checked={includeWeights}
                onChange={(e) => setIncludeWeights(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-foreground">Include question weights</span>
            </label>
            <label className="flex items-center gap-2 p-3 bg-background rounded cursor-pointer hover:bg-surfaceer transition-colors border border-border-muted">
              <input
                type="checkbox"
                checked={includeScoring}
                onChange={(e) => setIncludeScoring(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-foreground">Include scoring criteria</span>
            </label>
          </div>
        </div>

        {/* Add Custom Questions */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">Add Custom Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Category"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
            <input
              type="text"
              placeholder="Question"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              className="px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted md:col-span-2"
            />
          </div>
          <button
            onClick={addCustomQuestion}
            disabled={!newQuestion || !newCategory}
            className="mt-4 px-6 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Question
          </button>

          {customQuestions.length > 0 && (
            <div className="mt-4 space-y-2">
              <h3 className="text-lg font-semibold text-foreground">Custom Questions:</h3>
              {customQuestions.map((q) => (
                <div key={q.id} className="flex items-center gap-2 p-2 bg-background rounded">
                  <span className="text-foreground flex-1">
                    [{q.category}] {q.question}
                  </span>
                  <button
                    onClick={() => removeCustomQuestion(q.id)}
                    className="px-3 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vendor Management */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">Vendor Management</h2>
          <p className="text-foreground-secondary text-sm mb-4">
            Add vendors to evaluate. Each vendor will get their own sheet in the exported Excel file, with a summary sheet comparing all vendors.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              placeholder="Vendor name (e.g., Acme Corp, TechVendor Inc)"
              value={newVendor}
              onChange={(e) => setNewVendor(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addVendor()}
              className="px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
            <button
              onClick={addVendor}
              disabled={!newVendor}
              className="px-6 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Vendor
            </button>
          </div>

          {vendors.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground mb-2">Vendors to Evaluate ({vendors.length}):</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {vendors.map((vendor) => (
                  <div key={vendor} className="flex items-center justify-between gap-2 p-3 bg-background rounded border border-border-muted">
                    <span className="text-foreground flex-1">{vendor}</span>
                    <button
                      onClick={() => removeVendor(vendor)}
                      className="px-3 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {vendors.length === 0 && (
            <div className="p-4 bg-background rounded border border-border-muted">
              <p className="text-foreground-tertiary text-sm">
                No vendors added yet. Add vendors to create separate evaluation sheets in your Excel export.
              </p>
            </div>
          )}
        </div>

        {/* Preview and Export */}
        <div className="bg-surfaceer rounded-lg p-6 border border-border">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-accent">
                Generated Questions ({filteredQuestions.length})
              </h2>
              {excludedQuestionIds.size > 0 && (
                <p className="text-foreground-tertiary text-sm mt-1">
                  {excludedQuestionIds.size} question{excludedQuestionIds.size !== 1 ? 's' : ''} excluded
                </p>
              )}
            </div>
            <div className="flex gap-3">
              {excludedQuestionIds.size > 0 && (
                <button
                  onClick={resetExclusions}
                  className="px-4 py-2 bg-cream/20 text-foreground rounded-lg hover:bg-cream/30 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset Exclusions
                </button>
              )}
              {hasProAccess ? (
                <button
                  onClick={exportToExcel}
                  disabled={filteredQuestions.length === 0}
                  className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export to Excel
                </button>
              ) : (
                <Link
                  href="/pricing"
                  className="px-6 py-3 bg-accent-muted border-2 border-accent text-accent rounded-lg font-semibold hover:bg-accent/30 transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Upgrade to Export
                </Link>
              )}
              {commandCenterEnabled && !saved && filteredQuestions.length > 0 && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                  {saveError}
                </div>
              )}
            </div>
          </div>

          {filteredQuestions.length === 0 ? (
            <p className="text-foreground-tertiary text-center py-8">
              Select categories and priority levels to generate questions
            </p>
          ) : (
            <>
              {/* Bulk Actions */}
              <div className="mb-4 p-4 bg-background rounded-lg border border-border-muted">
                <h3 className="text-lg font-semibold text-foreground mb-3">Bulk Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* By Category */}
                  <div>
                    <p className="text-foreground-secondary text-sm mb-2">Select/Deselect by Category:</p>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((category) => {
                        const categoryQuestions = RFP_QUESTIONS.filter((q) => q.category === category);
                        const allSelected = categoryQuestions.every((q) => !excludedQuestionIds.has(q.id));
                        return (
                          <button
                            key={category}
                            onClick={() => allSelected ? deselectByCategory(category) : selectByCategory(category)}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              allSelected
                                ? 'bg-accent-muted text-accent border border-accent/40'
                                : 'bg-cream/10 text-foreground-tertiary border border-border hover:bg-cream/20'
                            }`}
                            title={allSelected ? `Deselect all ${category}` : `Select all ${category}`}
                          >
                            {category} ({categoryQuestions.length})
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* By Priority */}
                  <div>
                    <p className="text-foreground-secondary text-sm mb-2">Select/Deselect by Priority:</p>
                    <div className="flex flex-wrap gap-2">
                      {['Critical', 'High', 'Medium', 'Low'].map((priority) => {
                        const priorityQuestions = RFP_QUESTIONS.filter((q) => q.priority === priority);
                        const allSelected = priorityQuestions.every((q) => !excludedQuestionIds.has(q.id));
                        return (
                          <button
                            key={priority}
                            onClick={() => allSelected ? deselectByPriority(priority) : selectByPriority(priority)}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              allSelected
                                ? priority === 'Critical'
                                  ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                                  : priority === 'High'
                                  ? 'bg-accent-muted text-accent border border-accent/40'
                                  : priority === 'Medium'
                                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                                  : 'bg-green-500/20 text-green-400 border border-green-500/40'
                                : 'bg-cream/10 text-foreground-tertiary border border-border hover:bg-cream/20'
                            }`}
                            title={allSelected ? `Deselect all ${priority}` : `Select all ${priority}`}
                          >
                            {priority} ({priorityQuestions.length})
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border">
                    <th className="p-3 text-foreground font-semibold w-12">
                      <input
                        type="checkbox"
                        checked={filteredQuestions.length > 0 && excludedQuestionIds.size === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            resetExclusions();
                          } else {
                            const allIds = new Set(filteredQuestions.map(q => q.id));
                            setExcludedQuestionIds(allIds);
                          }
                        }}
                        className="w-4 h-4 cursor-pointer"
                        title="Select/Deselect all"
                      />
                    </th>
                    <th className="p-3 text-foreground font-semibold">
                      <span title="Mark critical requirements that must be met">Deal-Breaker</span>
                    </th>
                    <th className="p-3 text-foreground font-semibold">#</th>
                    <th className="p-3 text-foreground font-semibold">Category</th>
                    <th className="p-3 text-foreground font-semibold">Priority</th>
                    <th className="p-3 text-foreground font-semibold">Question</th>
                    {includeWeights && <th className="p-3 text-foreground font-semibold">Weight</th>}
                    <th className="p-3 text-foreground font-semibold">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuestions.map((q, index) => (
                    <tr key={q.id} className="border-b border-border-muted hover:bg-background transition-colors">
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={!excludedQuestionIds.has(q.id)}
                          onChange={() => toggleQuestionExclusion(q.id)}
                          className="w-4 h-4 cursor-pointer"
                          title={excludedQuestionIds.has(q.id) ? "Click to include" : "Click to exclude"}
                        />
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => toggleDealBreaker(q.id)}
                          className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                            dealBreakerQuestionIds.has(q.id)
                              ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                              : 'bg-cream/10 text-foreground-muted border border-border hover:bg-cream/20'
                          }`}
                          title={dealBreakerQuestionIds.has(q.id) ? "Remove as deal-breaker" : "Mark as deal-breaker"}
                        >
                          {dealBreakerQuestionIds.has(q.id) ? '⚠️ Critical' : 'Mark'}
                        </button>
                      </td>
                      <td className="p-3 text-foreground-tertiary">{index + 1}</td>
                      <td className="p-3 text-foreground">{q.category}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            q.priority === 'Critical'
                              ? 'bg-red-500/20 text-red-400'
                              : q.priority === 'High'
                              ? 'bg-accent-muted text-accent'
                              : q.priority === 'Medium'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-green-500/20 text-green-400'
                          }`}
                        >
                          {q.priority}
                        </span>
                      </td>
                      <td className="p-3 text-foreground">{q.question}</td>
                      {includeWeights && <td className="p-3 text-foreground-tertiary">{q.weight}</td>}
                      <td className="p-3 text-foreground-tertiary text-sm">{q.responseType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-surfaceer rounded-lg p-6 border border-border">
          <h3 className="text-xl font-bold text-accent mb-4">How to Use</h3>
          <ol className="list-decimal list-inside space-y-2 text-foreground-secondary">
            <li><strong>RFP Details</strong>: Enter your RFP title, description, and submission deadline for tracking and reporting</li>
            <li><strong>Pre-Built Templates</strong>: Click any template card to instantly load industry-standard question sets:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>SaaS Security Audit, API/Integration Platform, Cloud Infrastructure</li>
                <li>Consulting Firm, Data Analytics, Payment Processor, Healthcare SaaS</li>
                <li>AI/ML Platform, Enterprise Comprehensive, Quick Vendor Screening</li>
                <li>Auto-applies categories, priorities, and deal-breaker questions</li>
              </ul>
            </li>
            <li><strong>Email Templates</strong>: Download pre-written emails for vendor invitations, reminders, clarifications, and follow-ups</li>
            <li><strong>Question Templates</strong>: Filter by vendor type (SaaS, Consulting, Infrastructure, etc.)</li>
            <li><strong>Search</strong>: Find questions by keyword, category, or scoring criteria</li>
            <li><strong>Categories & Priorities</strong>: Select relevant categories and priority levels</li>
            <li><strong>Deal-Breakers</strong>: Click &quot;Mark&quot; button to flag critical requirements that must be met (vendors scoring &lt;7 are flagged in final report)</li>
            <li><strong>Bulk Actions</strong>: Quickly select/deselect all questions in a category or priority level</li>
            <li><strong>Custom Questions</strong>: Add your own questions specific to your needs</li>
            <li><strong>Multi-Vendor Evaluation</strong>: Add vendors to compare multiple vendors side-by-side:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Each vendor gets their own sheet in Excel</li>
                <li>&quot;Vendor Comparison&quot; sheet with automatic scoring by category</li>
                <li>&quot;Final Report&quot; tab with executive summary and recommendations</li>
                <li>Automatic ranking showing which vendor scores highest</li>
                <li>Deal-breaker analysis identifying vendors that fail critical requirements</li>
              </ul>
            </li>
            <li><strong>Save/Load Configuration</strong>: Save your RFP setup for reuse or load previous configurations</li>
            <li><strong>Export to Excel</strong>: Generate professional RFP with:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li><strong>Final Report Tab</strong>: Executive summary, vendor rankings, deal-breaker analysis, category breakdowns, recommendations, risk assessment, scoring legend</li>
                <li><strong>Vendor sheets</strong>: One per vendor with all questions and scoring</li>
                <li><strong>Vendor Comparison</strong>: Auto-calculated scores and rankings</li>
                <li><strong>Instructions</strong>: Complete guide for using the RFP</li>
                <li>Automatic formulas for weighted scoring</li>
                <li>Auto-filtering on all columns</li>
                <li>Status and deadline tracking</li>
              </ul>
            </li>
          </ol>

          <div className="mt-6 p-4 bg-accent-muted border border-border rounded">
            <p className="text-foreground font-semibold mb-2">🎯 Pro Tip:</p>
            <p className="text-foreground-secondary text-sm">
              Start with a pre-built template, mark your deal-breaker questions, add 2-3 vendors, then export to Excel.
              The Final Report tab will give you instant insights and recommendations!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
